/**
 * Campaign list + create + launch hooks. The list polls every 10s while
 * the tab is visible so sent_count / delivered_count tick up live as the
 * dispatcher and DLR receiver process the queue. (Faster than Dashboard's
 * 30s because operators stare at this view right after pressing "launch".)
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { getAccessToken } from '@/lib/auth';

export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'queued'
  | 'running'
  | 'paused'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface CampaignItem {
  id: number;
  uuid: string;
  name: string;
  status: CampaignStatus;
  sender_id: number;
  list_id: number;
  message_template: string;
  message_type: string;
  total_recipients: number;
  sent_count: number;
  delivered_count: number;
  failed_count: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  scheduled_at?: string;
  timezone?: string;
  /**
   * When true, the campaigns-worker rotates the SMS from-number round-robin
   * across ALL approved senders assigned to the tenant instead of using the
   * single sender_id. sender_id stays required as the default/fallback.
   */
  rotate_senders?: boolean;
}

export interface CampaignsResponse {
  items: CampaignItem[];
}

export interface CreateCampaignInput {
  name: string;
  sender_id: number;
  list_id: number;
  message_template: string;
  message_type?: 'marketing' | 'transactional' | '2fa';
  rotate_senders?: boolean;
}

export interface LaunchResult {
  total_recipients: number;
  enqueued: number;
  failed: number;
  first_error?: string;
}

const CAMPAIGNS_KEY = ['campaigns'] as const;

export function useCampaigns({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: CAMPAIGNS_KEY,
    queryFn: () => api.get<CampaignsResponse>('/v1/campaigns'),
    enabled,
    refetchInterval: enabled ? 10_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCampaignInput) => api.post<CampaignItem>('/v1/campaigns', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
    },
  });
}

export function useLaunchCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<LaunchResult>(`/v1/campaigns/${id}/launch`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
    },
  });
}

// ----- Single-campaign detail + messages + edit + delete -----

export interface CampaignMessage {
  id: number;
  uuid: string;
  phone_e164: string;
  body: string;
  status: string;
  segments: number;
  cost: number;
  error_code?: string;
  error_message?: string;
  provider_id?: number | null;
  /**
   * Mirrors providers.supports_dlr (migration 000085). When false,
   * `status='sent'` is terminal — the carrier never sends delivery
   * receipts (e.g. Brevo). Used by `messageDisplayStatus` so the
   * campaign-detail row renders "Delivered" instead of "Sending...".
   * Defaults to true on the server when the lookup fails or the
   * provider was deleted.
   */
  provider_supports_dlr: boolean;
  created_at: string;
  sent_at?: string;
  delivered_at?: string;
}

const campaignKey = (id: number | null) =>
  id ? (['campaigns', id] as const) : (['campaigns', 'none'] as const);
const campaignMessagesKey = (id: number | null) =>
  id ? (['campaigns', id, 'messages'] as const) : (['campaigns', 'none', 'messages'] as const);

export function useCampaign(id: number | null) {
  return useQuery({
    queryKey: campaignKey(id),
    queryFn: () => api.get<CampaignItem>(`/v1/campaigns/${id}`),
    enabled: id !== null,
    refetchInterval: (q) => {
      // Poll while in flight so the detail modal counters tick up live.
      const data = q.state.data as CampaignItem | undefined;
      if (!data) return false;
      return data.status === 'running' || data.status === 'queued' ? 5_000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

export function useCampaignMessages(id: number | null) {
  return useQuery({
    queryKey: campaignMessagesKey(id),
    queryFn: () => api.get<{ items: CampaignMessage[] }>(`/v1/campaigns/${id}/messages?limit=200`),
    enabled: id !== null,
    refetchInterval: 5_000,
    refetchIntervalInBackground: false,
  });
}

// ----- Detail-page extras (throughput chart + top errors) -------------------

export interface ThroughputPoint {
  /** RFC3339 bucket start. */
  bucket: string;
  sent: number;
  delivered: number;
  failed: number;
}

export interface ThroughputResponse {
  bucket_seconds: number;
  items: ThroughputPoint[];
}

export interface TopError {
  error_code: string;
  count: number;
  sample_message?: string;
}

const throughputKey = (id: number | null, bucketSecs: number) =>
  id
    ? (['campaigns', id, 'throughput', bucketSecs] as const)
    : (['campaigns', 'none', 'throughput', bucketSecs] as const);

const errorsKey = (id: number | null) =>
  id ? (['campaigns', id, 'errors'] as const) : (['campaigns', 'none', 'errors'] as const);

/**
 * Polls every 10s while the campaign is in flight. The detail page passes
 * the campaign so we can stop polling once it's terminal — wasting a query
 * every 10s on a completed campaign for the lifetime of the tab adds up.
 */
export function useCampaignThroughput(
  campaign: CampaignItem | null,
  bucketSecs = 60,
) {
  const id = campaign?.id ?? null;
  const live = campaign?.status === 'running' || campaign?.status === 'queued';
  return useQuery({
    queryKey: throughputKey(id, bucketSecs),
    queryFn: () =>
      api.get<ThroughputResponse>(`/v1/campaigns/${id}/throughput?bucket=${bucketSecs}`),
    enabled: id !== null,
    refetchInterval: live ? 10_000 : false,
    refetchIntervalInBackground: false,
  });
}

export function useCampaignErrors(id: number | null, limit = 5) {
  return useQuery({
    queryKey: errorsKey(id),
    queryFn: () => api.get<{ items: TopError[] }>(`/v1/campaigns/${id}/errors?limit=${limit}`),
    enabled: id !== null,
    staleTime: 30_000,
  });
}

export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: CreateCampaignInput & { id: number }) =>
      api.patch<CampaignItem>(`/v1/campaigns/${id}`, input),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
      void qc.invalidateQueries({ queryKey: campaignKey(data.id) });
    },
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/campaigns/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
    },
  });
}

export function useCancelCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/v1/campaigns/${id}/cancel`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
    },
  });
}

export function useDuplicateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<CampaignItem>(`/v1/campaigns/${id}/duplicate`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
    },
  });
}

export interface ScheduleInput {
  /** RFC3339 timestamp; the backend rejects past times. */
  scheduled_at: string;
  /** IANA timezone — used by the UI for display, the daemon compares UTC. */
  timezone?: string;
}

export function useScheduleCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ScheduleInput & { id: number }) =>
      api.post<CampaignItem>(`/v1/campaigns/${id}/schedule`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
    },
  });
}

export function useUnscheduleCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/v1/campaigns/${id}/unschedule`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CAMPAIGNS_KEY });
    },
  });
}

/**
 * Triggers a CSV download for a campaign's messages. Same authed-blob
 * pattern as downloadReportCSV — `<a href>` can't carry the Bearer
 * header so we fetch as a blob, click a synthetic anchor, then revoke.
 */
export async function downloadCampaignCSV(id: number, filename?: string): Promise<void> {
  const apiBase =
    (typeof import.meta.env.VITE_API_BASE_URL === 'string' &&
      import.meta.env.VITE_API_BASE_URL) ||
    'http://localhost:8080';
  const token = getAccessToken();
  const response = await fetch(`${apiBase}/v1/campaigns/${id}/messages.csv`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`CSV export failed (${response.status})`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename ?? `ezsender-campaign-${id}-messages.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
